import { DecimalPipe } from '@angular/common';
import {
  Directive,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  LOCALE_ID,
  Renderer2,
  forwardRef,
  inject,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';

const DEFAULT_NUMBER_FORMAT = '1.2-2';
const MAX_FRACTION_DIGITS_REGEXP = /^\d+\.\d+-(\d+)$/;

type NumberInputValue = string | null;

@Directive({
  selector: 'input[diNumberInput]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NumberInputDirective),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => NumberInputDirective),
      multi: true,
    },
  ],
  standalone: false,
})
export class NumberInputDirective implements ControlValueAccessor, Validator {
  private elementRef = inject<ElementRef<HTMLInputElement>>(ElementRef);
  private renderer = inject(Renderer2);
  private locale = inject(LOCALE_ID);

  private _format = DEFAULT_NUMBER_FORMAT;
  private _value: NumberInputValue = null;
  private lastValueValid = false;
  private hasInvalidInput = false;
  private hasTooManyDecimals = false;
  private isFocused = false;
  private onControlValueChange?: (value: NumberInputValue) => void;
  private onControlValueTouched?: () => void;

  @HostBinding('disabled') disabled = false;
  @HostBinding('autocomplete') @Input() autocomplete = 'off';

  @Input('diNumberInput') set format(value: string) {
    if (!value) {
      this._format = DEFAULT_NUMBER_FORMAT;
    } else {
      if (!MAX_FRACTION_DIGITS_REGEXP.test(value)) {
        throw new Error(`${value} format value for [diNumberInput] is not valid`);
      }
      this._format = value;
    }

    const currValue = this.getInputValue();
    this.handleInputValue(currValue);
  }

  set value(value: NumberInputValue) {
    this._value = value;
    if (value === null || (typeof value === 'string' && value.trim() !== '')) {
      this.clearValidationErrors();
      this.updateInput(value);
    } else {
      this.lastValueValid = false;
    }
  }

  get value(): NumberInputValue {
    return this._value;
  }

  @HostListener('input', ['$event'])
  onInput(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.handleInputValue(value);
  }

  private handleInputValue(value: string) {
    // Handle empty values immediately
    if (this.isEmptyValue(value)) {
      this._value = '';
      this.clearValidationErrors();
      this.onControlValueChange?.('');
      // Mark as touched to trigger validation immediately
      this.onControlValueTouched?.();
      return;
    }

    const { normalizedValue, invalid, tooManyDecimals } = this.parseNumber(value);
    if (invalid || tooManyDecimals) {
      this.setValidationErrors(invalid, tooManyDecimals);
      this._value = value;
      this.onControlValueChange?.(value);
      // Mark as touched to trigger validation immediately
      this.onControlValueTouched?.();
    } else {
      this.clearValidationErrors();
      this._value = normalizedValue ?? '';
      this.onControlValueChange?.(normalizedValue ?? '');
    }
  }

  @HostListener('blur')
  onBlur() {
    this.isFocused = false;
    this.onControlValueTouched?.();

    const currentInputValue = this.getInputValue();

    if (this.isEmptyValue(currentInputValue)) {
      if (this._value !== '') {
        this._value = '';
        this.onControlValueChange?.('');
      }
      this.clearValidationErrors();
      this.updateInput('');
      return;
    }

    // Only format if there's no error
    const hasError = this.hasInvalidInput || this.hasTooManyDecimals;
    this.updateInput(this._value, !hasError);
  }

  @HostListener('focus')
  onFocus() {
    this.isFocused = true;
    if (!this.disabled) {
      let editValue = this._value == null ? '' : String(this._value);

      // Convert dot to locale's decimal separator for editing
      const localeSeparator = this.getLocaleDecimalSeparator();
      if (localeSeparator !== '.' && editValue.includes('.')) {
        editValue = editValue.replace('.', localeSeparator);
      }

      this.renderer.setProperty(this.elementRef.nativeElement, 'value', editValue);
      setTimeout(() => {
        this.elementRef.nativeElement.select();
      }, 0);
    }
  }

  writeValue(value: string | null): void {
    this._value = value;
    if (!this.isFocused) {
      // If value is null/empty, keep it empty (don't format to 0)
      if (this.isEmptyValue(value)) {
        this.clearValidationErrors();
        this.updateInput('');
        return;
      }

      const { invalid, tooManyDecimals } = this.parseNumber(value || '');

      if (invalid || tooManyDecimals) {
        this.setValidationErrors(invalid, tooManyDecimals);
      } else {
        this.clearValidationErrors();
      }

      const shouldFormat = !invalid && !tooManyDecimals;
      this.updateInput(value, shouldFormat);
    }
  }

  registerOnChange(fn: (value: NumberInputValue) => void): void {
    this.onControlValueChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onControlValueTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    // Format the value when disabled, or when enabled but not focused
    if (this._value !== null && (disabled || !this.isFocused)) {
      this.updateInput(this._value, true);
    }
  }

  validate(): ValidationErrors | null {
    if (this.hasTooManyDecimals) {
      const match = this._format.match(MAX_FRACTION_DIGITS_REGEXP);
      const maxFractionDigits = match ? parseInt(match[1], 10) : 2;
      return { maxDecimalPlaces: { maxDecimalPlaces: maxFractionDigits } };
    }

    if (this.hasInvalidInput) {
      return { number: true };
    }

    const result =
      this.lastValueValid || this.value === null || this.value === undefined || this.value === ''
        ? null
        : { number: true };
    return result;
  }

  private updateInput(value: NumberInputValue, format = true): void {
    if (this.isEmptyValue(value)) {
      this.renderer.setProperty(this.elementRef.nativeElement, 'value', '');
      return;
    }

    const displayValue = format ? this.formatNumber(value) : value;
    this.renderer.setProperty(this.elementRef.nativeElement, 'value', displayValue);
  }

  private getInputValue(): string {
    return this.elementRef.nativeElement.value;
  }

  private isEmptyValue(value: NumberInputValue): boolean {
    return value === null || value === undefined || value === '';
  }

  private clearValidationErrors(): void {
    this.lastValueValid = true;
    this.hasInvalidInput = false;
    this.hasTooManyDecimals = false;
  }

  private setValidationErrors(
    invalid: boolean | undefined,
    tooManyDecimals: boolean | undefined,
  ): void {
    this.lastValueValid = false;
    this.hasInvalidInput = invalid || false;
    this.hasTooManyDecimals = tooManyDecimals || false;
  }

  private getLocaleDecimalSeparator(): string {
    const formatter = new Intl.NumberFormat(this.locale);
    const parts = formatter.formatToParts(1.1);
    const decimalPart = parts.find((part) => part.type === 'decimal');
    return decimalPart ? decimalPart.value : '.';
  }

  private parseNumber(input: string | null | undefined): {
    normalizedValue?: string | null;
    invalid?: boolean;
    tooManyDecimals?: boolean;
  } {
    const inputString = input == null ? '' : String(input);
    if (input == null || inputString.trim() === '') {
      return { normalizedValue: null };
    }

    let normalizedInput = inputString.replace(/\s/g, '');

    // Find the last separator (dot or comma) - it's the decimal separator
    // All other separators are treated as thousand separators and removed
    const lastDot = normalizedInput.lastIndexOf('.');
    const lastComma = normalizedInput.lastIndexOf(',');
    const lastSeparatorIndex = Math.max(lastDot, lastComma);

    if (lastSeparatorIndex > -1) {
      // Split at last separator, remove all other separators, then rejoin with dot
      const beforeSeparator = normalizedInput.slice(0, lastSeparatorIndex).replace(/[.,]/g, '');
      const afterSeparator = normalizedInput.slice(lastSeparatorIndex + 1);
      normalizedInput = beforeSeparator + '.' + afterSeparator;
    }

    const numberValue = Number(normalizedInput);
    if (isNaN(numberValue)) {
      return { invalid: true };
    }

    const match = this._format.match(MAX_FRACTION_DIGITS_REGEXP);
    const maxFractionDigits = match ? parseInt(match[1], 10) : 2;

    const decimalParts = normalizedInput.split('.');
    if (decimalParts.length === 2 && decimalParts[1].length > maxFractionDigits) {
      return { tooManyDecimals: true };
    }

    return { normalizedValue: normalizedInput };
  }

  private formatNumber(value: NumberInputValue): string {
    if (this.isEmptyValue(value)) {
      return '';
    }

    const numberValue = Number(value);
    if (Number.isNaN(numberValue)) {
      return '';
    }

    if (Number.isInteger(numberValue)) {
      return new DecimalPipe(this.locale).transform(value, '1.0-0') || '';
    }

    const formattedValue = new DecimalPipe(this.locale).transform(value, this._format);

    return formattedValue || '';
  }
}
